import { Dispatch } from 'redux';
import { AxiosResponse } from 'axios';
import {
    SELECTED_SINGIN_FORM,
    SIGN_IN_REQUEST,
    SIGN_IN_SUCCESS,
    SIGN_IN_FAILURE,
    CURRENT_USER,
    SIGN_OUT,
    SIGN_IN_ERROR_CLEAN
} from '@app/store/constants';
import { loginForms } from '@app/enums';
import { DispatchFunction, IToken, IUserData, SetErrorCode } from '@app/models';
import { authService, basicService, sessionService } from '@app/services';
import { handleShellConfig } from '@app/store/actions';
import { localStore, USER_DISABLED_KASEYA_ONE_ERROR } from '@app/helpers';
import { KASEYA_ONE_TOKEN } from '@app/constants';

export function selectSignInForm(form: loginForms) {
    return {
        payload: form,
        type: SELECTED_SINGIN_FORM
    };
}

export function signIn(username: string, password: string, onboardToken: string = '', rememberMe?: boolean): DispatchFunction {
    if (onboardToken === '') {
        return (dispatch: Dispatch) => {
            dispatch(signInRequest(username));
            authService.signin(username, password).then(
                (response: AxiosResponse<IToken>) => {
                    dispatch(signInErrorClean());
                    setSignInData(response, dispatch, rememberMe);
                    dispatch<any>(handleShellConfig(redirectAfterShellConfigLoads));
                },
                (error: AxiosResponse) => {
                    // TODO: Uncomment when we will set up dev variable
                    // console.log({ error });
                    dispatch(signInFailure(error));
                }
            );
        };
    } else {
        return (dispatch: Dispatch) => {
            authService.onboard(onboardToken).then(
                (response: AxiosResponse<IToken>) => {
                    dispatch(signInErrorClean());
                    setSignInData(response, dispatch, rememberMe);
                },
                (error: AxiosResponse) => {
                    dispatch(signInFailure(error));
                }
            );
        };
    }
}

export function signInKaseyaOne(
    setErrorKaseyaOneCode: SetErrorCode,
    setIsPageLoading: (is: boolean) => void,
    setIsLoading: (is: boolean) => void,
    setLoginForm: (is: loginForms) => void,
    username?: string,
    password?: string
): DispatchFunction {
    return (dispatch: Dispatch) => {
        authService.signInKaseyaOne(username, password).then(
            (response: AxiosResponse<IToken>) => {
                setIsPageLoading(false);
                setIsLoading(false);
                dispatch(signInErrorClean());
                setSignInData(response, dispatch);
                dispatch<any>(handleShellConfig(redirectAfterShellConfigLoads));
            },
            (error: AxiosResponse) => {
                const errorText = error.request.responseText ? JSON.parse(error.request.responseText).error : '';

                if (errorText === USER_DISABLED_KASEYA_ONE_ERROR) {
                    setLoginForm(loginForms.credsKaseyaOne);
                }

                setIsPageLoading(false);
                setIsLoading(false);
                setErrorKaseyaOneCode(errorText);
                dispatch(signInFailure(error));
            }
        );
    };
}

function redirectAfterShellConfigLoads() {
    basicService.redirectAfterSignin();
}

function signInRequest(username: string) {
    return {
        payload: username,
        type: SIGN_IN_REQUEST
    };
}

export function signInFailure(error: AxiosResponse) {
    return {
        payload: error,
        type: SIGN_IN_FAILURE
    };
}

export function elevateSignin(userId: string, methods: string): DispatchFunction {
    return (dispatch: Dispatch) => {
        authService.elevate(userId, methods).then(
            (response: AxiosResponse<IToken>) => {
                setSignInData(response, dispatch);
                dispatch<any>(handleShellConfig());
                basicService.redirectAfterSignin();
            },
            (error: AxiosResponse) => {
                dispatch(signInFailure(error));
            }
        );
    };
}

export function k1Signin(accessToken: string): DispatchFunction {
    return (dispatch: Dispatch) => {
        authService.signInK1(accessToken).then(
            (response: AxiosResponse<IToken>) => {
                setSignInData(response, dispatch);
                dispatch<any>(handleShellConfig());
                basicService.redirectAfterSignin();
            },
            (error: AxiosResponse) => {
                dispatch(signInFailure(error));
            }
        );
    };
}

function signInSuccess(token: IToken) {
    return {
        payload: token,
        type: SIGN_IN_SUCCESS
    };
}

export function signInErrorClean() {
    return {
        type: SIGN_IN_ERROR_CLEAN
    };
}

export function setSignInData(response: AxiosResponse<IToken>, dispatch: Dispatch, rememberMe = true) {
    const data = response.data;
    const user = authService.modelUserData(data);
    const kaseyaOneToken = data.k1_access_token;

    dispatch(signInSuccess(data));
    authService.handleLocalStorage(data);
    authService.setUserData(user);

    if (kaseyaOneToken) {
        localStore.setItem(KASEYA_ONE_TOKEN, kaseyaOneToken);
    } else {
        localStorage.removeItem(KASEYA_ONE_TOKEN);
    }

    if (rememberMe) {
        sessionService.addSession([
            {
                username: data.nameid,
                email: data.email,
                displayName: data.dn,
                userUuid: data.uid,
                lastVisited: Date.now()
            }
        ]);
    }

    dispatch(currentUser(user));
}

export function signOut(unauthorized: boolean) {
    basicService.clearUserLocalStorageData();
    if (!unauthorized) {
        authService
            .signOut()
            .then((res) => {
                if (res.data.itcLogoutUrl) {
                    window.location.href = `${res.data.itcLogoutUrl}${window.location.origin}/signin`;
                } else if (res.data.url) {
                    window.location.href = res.data.url;
                } else {
                    basicService.redirect('/');
                }
            })
            .catch(() => {
                basicService.redirect('/');
            });
    } else {
        basicService.redirect('/');
    }

    return { type: SIGN_OUT };
}

export function currentUser(user: IUserData) {
    return {
        payload: user,
        type: CURRENT_USER
    };
}
